import { ReactNode } from 'react';
import cn from 'classnames';
import { MdOutlineAttribution } from 'react-icons/md';
import { ShiftSiblingData } from '@lp/features/Scheduler/types';
import { isDifferentHrSource } from '@lp/features/Scheduler/utils';
import { CCShiftOptionType } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/types';
import { singleCCShiftTypes } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/constants';
import { shiftTypeNames } from '@/constants/schedule.constants';
import { CCShift, CCShiftType, ShiftStatus } from '@/api/apis/resources/schedule';

export const getCCSiblingData = (shift: CCShift, prevShift?: CCShift): ShiftSiblingData => {
  const isSingle = singleCCShiftTypes.includes(shift.shiftType);
  const sameShiftType = shift.shiftType === prevShift?.shiftType;

  if (isSingle || !sameShiftType) return { hasPrev: false };

  if (shift.shiftType === CCShiftType.OnCall) {
    if (shift.isManager !== prevShift?.isManager) return { hasPartialPrev: true };
  }

  if (isDifferentHrSource(shift, prevShift)) return { hasPartialPrev: true };

  return { hasPrev: true };
};

export const getShiftFromShiftOption = (shiftOption?: CCShiftOptionType) => {
  if (!shiftOption) return undefined;

  switch (shiftOption) {
    case CCShiftOptionType.OnDutyCall:
      return { shiftType: CCShiftType.OnCall, isManager: true };
    case CCShiftOptionType.OnCall:
      return { shiftType: CCShiftType.OnCall, isManager: false };
    default:
      return { shiftType: CCShiftType[shiftOption], isManager: null };
  }
};

export const getShiftOptionFromShift = (shift?: CCShift) => {
  if (!shift) return undefined;
  if (shift.shiftType === CCShiftType.OnCall && shift.isManager) return CCShiftOptionType.OnDutyCall;

  return CCShiftOptionType[CCShiftType[shift.shiftType]];
};

export const getCCShiftColors = (shiftData?: CCShift) => {
  const { shiftType, shiftStatus, isPreSchedule } = shiftData ?? {};
  const isPlanning = shiftStatus === ShiftStatus.InPlanningProcess;
  const borderStyle = isPreSchedule ? 'border' : 'border border-dashed';

  if (isPlanning || isPreSchedule) {
    switch (shiftType) {
      case CCShiftType.OnCall:
        return {
          bg: 'bg-success-40/20',
          text: 'text-success-5',
          secondaryText: 'text-success-20',
          border: cn(borderStyle, 'border-success-10'),
          icon: 'text-success-40',
        };
      case CCShiftType.Vacation:
      case CCShiftType.Sick:
        return {
          bg: 'bg-warning-40/20',
          text: 'text-warning-5',
          secondaryText: 'text-warning-5',
          border: 'border-transparent',
          icon: 'text-warning-40/20',
        };
      case CCShiftType.Unavailable:
        return {
          bg: 'bg-danger-40/10',
          text: 'text-danger-5',
          secondaryText: 'text-danger-20',
          border: 'border border-danger-20',
          icon: 'text-danger-30',
        };
      case CCShiftType.WeekendOff:
        return {
          bg: 'bg-base-50/50',
          text: 'text-base-10',
          secondaryText: 'text-base-20',
          border: cn(borderStyle, 'border-base-20'),
          icon: 'text-base-30',
        };
      case CCShiftType.NoCall:
        return {
          bg: 'bg-transparent',
          text: 'text-base-25',
          secondaryText: 'text-base-40',
          border: cn(borderStyle, 'border-base-20'),
          icon: 'text-base-30',
        };
      default:
        return {
          bg: 'bg-transparent',
          text: 'text-base-25',
          secondaryText: 'text-base-70',
          border: 'border-transparent',
          icon: 'text-base-5',
        };
    }
  }

  switch (shiftType) {
    case CCShiftType.OnCall:
      return {
        bg: 'bg-success-40',
        text: 'text-white',
        secondaryText: 'text-success-70',
        border: 'border-transparent',
        icon: 'text-success-40',
      };
    case CCShiftType.Vacation:
    case CCShiftType.Sick:
      return {
        bg: 'bg-warning-40/20',
        text: 'text-warning-5',
        secondaryText: 'text-warning-5',
        border: 'border-transparent',
        icon: 'text-warning-40/20',
      };
    case CCShiftType.Unavailable:
      return {
        bg: 'bg-danger-40/20',
        text: 'text-danger-5',
        secondaryText: 'text-danger-5',
        border: 'border-transparent',
        icon: 'text-danger-30',
      };
    case CCShiftType.WeekendOff:
      return {
        bg: 'bg-base-50',
        text: 'text-base-10',
        secondaryText: 'text-base-20',
        border: 'border-transparent',
        icon: 'text-base-30',
      };
    case CCShiftType.NoCall:
    default:
      return {
        bg: 'bg-transparent',
        text: 'text-base-25',
        secondaryText: 'text-base-70',
        border: 'border-transparent',
        icon: 'text-base-5',
      };
  }
};

const getTextWithIcon = (text: string, icon: ReactNode) => (
  <>
    {text}
    {icon}
  </>
);

export const getCCShiftTypePrimaryText = (shiftData: CCShift) => {
  const { shiftType, isManager } = shiftData;
  const text = shiftTypeNames[shiftType] ?? null;

  switch (shiftData.shiftType) {
    case CCShiftType.OnCall:
      return isManager ? getTextWithIcon(text, <MdOutlineAttribution size={12} />) : text;
    default:
      return text;
  }
};

export const getShiftOptionIconColor = (shiftOption?: CCShiftOptionType) => {
  switch (shiftOption) {
    case CCShiftOptionType.OnDutyCall:
    case CCShiftOptionType.OnCall:
      return 'text-success-40';
    case CCShiftOptionType.Vacation:
    case CCShiftOptionType.Sick:
      return 'text-warning-40';
    case CCShiftOptionType.Unavailable:
      return 'text-danger-40';
    case CCShiftOptionType.NoCall:
    case CCShiftOptionType.WeekendOff:
      return 'text-base-30';
    default:
      return 'text-base-5';
  }
};

const CC_WD_SHIFT_TYPES = new Set([CCShiftType.Unavailable, CCShiftType.Sick, CCShiftType.Vacation]);

export const getCCShowHrIcon = (shiftData: CCShift): boolean =>
  !!shiftData.isHrShift && CC_WD_SHIFT_TYPES.has(shiftData.shiftType);
